import { Component, ElementRef, Input, OnDestroy, ViewChild, AfterViewInit, ViewEncapsulation } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MyImage } from '../../models/my-image.model';
import { TenantImage } from '../../models/tenant-image.model';
import { UnsplashImage } from '../../models/unsplash-image.model';
import { AiImage } from '../../models/ai-image.model';
import Cropper from 'cropperjs';
import { MyImageApiService } from 'app/services/image-library/my-image-api.service';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'app-image-cropper',
    templateUrl: './image-cropper.component.html',
    styleUrls: ['./image-cropper.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ImageCropperDialogComponent extends UnsubscribeOnDestroy implements AfterViewInit, OnDestroy {
    @Input()
    selectedImage!: MyImage | TenantImage | UnsplashImage | AiImage;

    @Input()
    previewImageWidth?: number;
    @Input()
    previewImageHeight?: number;
    @Input()
    freeCrop: boolean = false;

    @Input()
    onCropped?: (file: File) => void;

    @ViewChild('image') imageElement!: ElementRef<HTMLImageElement>;
    private cropper!: Cropper;

    get imageSource(): string {
        const image = this.selectedImage as AiImage;
        if (image?.url) {
            return image.url;
        }

        if (image?.image) {
            return `data:image/png;base64,${image.image}`;
        }

        return '';
    }

    constructor(public dialogRef: MatDialogRef<ImageCropperDialogComponent>, public myImageApiService: MyImageApiService) {
        super();
    }

    ngAfterViewInit(): void {
        this.cropper = new Cropper(this.imageElement.nativeElement, {
            viewMode: 2,
            dragMode: 'move',
            movable: true,
            cropBoxMovable: true,
            cropBoxResizable: true,
            zoomable: true,
            background: true,
            responsive: true,
            autoCrop: true,
            aspectRatio: this.freeCrop ? NaN : this.getAspectRatio(),
            ready: () => this.initializeCropBox(),
        });
    }

    private getAspectRatio(): number {
        if (this.previewImageWidth && this.previewImageHeight) {
            return this.previewImageWidth / this.previewImageHeight;
        }

        return 1;
    }

    private initializeCropBox(): void {
        const canvasData = this.cropper.getCanvasData();
        const maxCropBoxWidth = canvasData.width * 0.95;
        const maxCropBoxHeight = canvasData.height * 0.95;
        let cropBoxWidth: number;
        let cropBoxHeight: number;

        if (this.freeCrop) {
            cropBoxWidth = maxCropBoxWidth;
            cropBoxHeight = maxCropBoxHeight;
        } else {
            const aspectRatio = this.getAspectRatio();
            const maxCropAspectRatio = maxCropBoxWidth / maxCropBoxHeight;

            if (maxCropAspectRatio > aspectRatio) {
                cropBoxHeight = maxCropBoxHeight;
                cropBoxWidth = cropBoxHeight * aspectRatio;
            } else {
                cropBoxWidth = maxCropBoxWidth;
                cropBoxHeight = cropBoxWidth / aspectRatio;
            }
        }

        this.cropper.setCropBoxData({
            left: canvasData.left + (canvasData.width - cropBoxWidth) / 2,
            top: canvasData.top + (canvasData.height - cropBoxHeight) / 2,
            width: cropBoxWidth,
            height: cropBoxHeight,
        });
    }

    zoomIn(): void {
        this.cropper?.zoom(0.1);
    }

    zoomOut(): void {
        this.cropper?.zoom(-0.1);
    }

    rotate(): void {
        this.cropper?.rotate(90);
    }

    reset(): void {
        this.cropper?.reset();
        this.initializeCropBox();
    }

    save(): void {
        // freeCrop: keep crop aspect ratio and only cap max size (forced width/height would stretch + over-compress).
        // fixed crop: export exactly to the configured preview size.
        const canvasOptions: Cropper.GetCroppedCanvasOptions = {
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'high',
        };

        if (this.freeCrop) {
            if (this.previewImageWidth) {
                canvasOptions.maxWidth = this.previewImageWidth;
            }
            if (this.previewImageHeight) {
                canvasOptions.maxHeight = this.previewImageHeight;
            }
        } else {
            canvasOptions.width = this.previewImageWidth;
            canvasOptions.height = this.previewImageHeight;
        }

        this.cropper
            .getCroppedCanvas(canvasOptions)
            ?.toBlob(
                (blob) => {
                    if (!blob) return;

                    let croppedImage: File;

                    if (this.selectedImage instanceof MyImage || this.selectedImage instanceof TenantImage) {
                        croppedImage = new File([blob], this.selectedImage.originalName, {
                            type: this.selectedImage.mimeType,
                        });
                    } else {
                        croppedImage = new File([blob], 'file', { type: 'image/jpeg' });
                    }

                    this.onCropped?.(croppedImage);

                    this.myImageApiService.uploadingCroppedImage$.pipe(takeUntil(this._unsubscribeAll)).subscribe((isUploadingImage) => {
                        if (!isUploadingImage) {
                            this.dialogRef.close(croppedImage);
                        }
                    });
                },
                (this.selectedImage as MyImage | TenantImage).mimeType || 'image/jpeg',
                1
            );
    }

    ngOnDestroy(): void {
        this.cropper?.destroy();
        super.ngOnDestroy();
    }
}
